import { Component, computed, inject, output, signal } from '@angular/core'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { servicePointGroupEntitiesList, ServicePointGroupUpsertRequest, ServicePointGroupWithRefs, ServicePointGroupEntityAttribute } from './service-point-group.types'
import { ModalHeaderComponent } from '../modals/modal-header/modal-header.component'
import { ModalBodyComponent } from '../modals/modal-body/modal-body.component'
import { ModalFooterComponent } from '../modals/modal-footer/modal-footer.component'
import { FieldColumnsComponent } from '../field-columns/field-columns.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { EntitySelectorComponent } from '../entity-selector/entity-selector.component'
import { EntityRef, EntityRefWithUID, ServicePointIdReference } from 'src/app/shared/models/referenceData'
import { toSignal } from '@angular/core/rxjs-interop'
import { EntityFilter, EntityFilterState } from 'src/app/shared/services/filter/entity-filter'
import { StringFilter, StringFilterState } from 'src/app/shared/services/filter/string-filter'
import { ServicePointFilter, ServicePointFilterState } from 'src/app/shared/services/filter/service-point-filter'
import { ServicePointSelectorComponent } from '../service-point-selector/service-point-selector.component'
import { ServicePointSearchFilter, ServicePointSearchFilterState } from 'src/app/shared/services/filter/service-point-search-filter'
import { TextInputComponent } from '../text-input/text-input.component'

export type ServicePointGroupModalSaveArgs = Readonly<{
  request: ServicePointGroupUpsertRequest
  id?: string
}>

export type ServicePointGroupModalInitArgs = Readonly<{
  data: Partial<ServicePointGroupWithRefs>
  onSave: (args: ServicePointGroupModalSaveArgs) => void
  onCancel: () => void
}>

@Component({
  imports: [ModalHeaderComponent, ModalBodyComponent, ModalFooterComponent, FieldColumnsComponent, LabeledPropertyComponent, EntitySelectorComponent, ServicePointSelectorComponent, TextInputComponent],
  selector: 'app-service-point-group-modal',
  template: `
    <div app-modal-header>{{ action() }} Service Point Group</div>

    @if (this.modalState()) {
      <div app-modal-body>
        <div app-field-columns>
          <div app-labeled-property [label]="groupNameFilter()!.config.meta.fieldDisplay">
            <div
                app-text-input
                [value]="groupNameFilter()!.values[0]"
                (valueChange)="handleGroupName($event)"
            ></div>
          </div>
        </div>

        <div app-field-columns>
          @if (servicePointFilter()) {
            <div app-labeled-property [label]="servicePointFilter()!.config.meta.fieldDisplay">
              <div
                  app-service-point-selector
                  [slim]="true"
                  [allowPartial]="true"
                  [selectedServicePoints]="servicePointFilter()!.servicePoints"
                  [selectedPartialValue]="servicePointSearchFilter()?.value ?? null"
                  (valueChange)="handleServicePoint($event)"
                  (partialValueChange)="handleServicePointSearch($event)"
                  [config]="{ allowMultiple: true }"
              >
              </div>
            </div>
          }

          @for (e of entityList; track e) {
            @if (entityFilter(e)) {
              <div app-labeled-property [label]="entityFilter(e)!.config.meta.fieldDisplay">
                <div app-entity-selector
                    [slim]="true"
                    [label]="entityFilter(e)!.config.meta.fieldDisplay"
                    [selectedEntities]="entityFilter(e)!.entities"
                    (valueChange)="handleEntity(entityFilter(e)!, $event)"
                    [config]="entityFilter(e)!.config"
                ></div>
              </div>
            }
          }
        </div>
      </div>
    }

    <div app-modal-footer>
      <button
          class="action-btn primary"
          [disabled]="!canSave()"
          (click)="save()"
      >
        {{ action() }}
      </button>
      <button class="action-btn" (click)="onCancel.emit()">Cancel</button>
    </div>
  `,
})
export class ServicePointGroupModal {
  private readonly filterService = inject(FilterService)

  protected readonly servicePointGroupUid = signal<string | undefined>(undefined)
  protected readonly modalState = toSignal(this.filterService.state(ServicePointGroupModal.name))

  protected readonly onCancel = output()
  protected readonly onSave = output<ServicePointGroupModalSaveArgs>()

  protected readonly action = computed((): string => this.servicePointGroupUid() ? 'Update': 'Create')

  protected readonly entityList = servicePointGroupEntitiesList

  public init({ data, onSave, onCancel }: ServicePointGroupModalInitArgs) {
    this.onSave.subscribe(onSave)
    this.onCancel.subscribe(onCancel)
    void this.filterService.importServicePointGroupFilters(ServicePointGroupModal.name, data)
    if (data.service_point_group_uid) {
      this.servicePointGroupUid.set(data.service_point_group_uid)
    }
  }

  protected canSave(): boolean {
    return Boolean(this.groupNameFilter()?.values[0])
  }

  protected entityFilter(e: ServicePointGroupEntityAttribute): EntityFilterState | undefined {
    return this.modalState()?.[e]?.state as EntityFilterState | undefined
  }

  protected handleEntity(e: EntityFilterState, evt: EntityRef) {
    this.filterService.dispatch(EntityFilter.AddEntityRefsAction(
      { entities: [evt] },
      e.config.meta,
    ))
  }

  protected groupNameFilter(): StringFilterState | undefined {
    return this.modalState()?.['service_point_group_name']?.state as StringFilterState | undefined
  }

  protected handleGroupName(evt: string) {
    this.filterService.dispatch(StringFilter.SetStringsAction(
      { values: [evt] },
      this.groupNameFilter()!.config.meta,
    ))
  }

  protected servicePointFilter(): ServicePointFilterState | undefined {
    return this.modalState()?.['service_point_uid']?.state as ServicePointFilterState | undefined
  }

  protected handleServicePoint(evt: ServicePointIdReference) {
    this.filterService.dispatch(ServicePointFilter.AddServicePointsAction(
      { servicePoints: [evt] },
      this.servicePointFilter()!.config.meta,
    ))
  }

  protected servicePointSearchFilter(): ServicePointSearchFilterState | undefined {
    return this.modalState()?.['service_point_id_search']?.state as ServicePointSearchFilterState | undefined
  }

  protected handleServicePointSearch(evt: string) {
    this.filterService.dispatch(ServicePointSearchFilter.SetServicePointSearchFilterValueAction(
      { value: evt },
      this.servicePointSearchFilter()!.config.meta,
    ))
  }
  
  private entityUids(attribute: ServicePointGroupEntityAttribute): readonly string[] {
    return this.entityFilter(attribute)?.entities.map(r => (r as EntityRefWithUID).entity_uid) ?? []
  }

  protected save() {
    this.onSave.emit({
      ...this.servicePointGroupUid() ? { id: this.servicePointGroupUid() } : {},
      request: {
        service_point_group_name: this.groupNameFilter()?.values[0] ?? '',
        service_point_uid: this.servicePointFilter()?.servicePoints.map(r => r.service_point_uid) ?? [],
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        service_point_id_search: this.servicePointSearchFilter()?.value || undefined,
        company_code: this.entityFilter('company_code')?.entities.map(r => r.entity_code) ?? [],
        control_area_uid: this.entityUids('control_area_uid'),
        cpnode_uid: this.entityUids('cpnode_uid'),
        delivery_class_uid: this.entityUids('delivery_class_uid'),
        eldc_uid: this.entityUids('eldc_uid'),
        ldc_uid: this.entityUids('ldc_uid'),
        location_uid: this.entityUids('location_uid'),
        loss_class_uid: this.entityUids('loss_class_uid'),
        meter_type_uid: this.entityUids('meter_type_uid'),
        profile_class_uid: this.entityUids('profile_class_uid'),
        retailer_uid: this.entityUids('retailer_uid'),
        service_point_source_uid: this.entityUids('service_point_source_uid'),
        supply_class_uid: this.entityUids('supply_class_uid'),
      },
    })
  }
}
